// auto-link-prewarm.ts — pre-warm probe, atob classifier, retry-backoff helper.
// Isolated from auto-link-controller to keep tests free of React imports.
//
// Iter-12 W14: codex xhigh + opus adversarial converged on the auto-link
// classifier mis-labeling atob DOMException as "Lambda warm". A decode
// failure means the response body is non-base64 (real, persistent) — NOT
// that the Lambda just responded fast. Treating it as warm + zero-backoff
// retry collapsed the linking phase in <1s on Mac cold-start, transitioning
// auto-link → error before the renderer ever left /room/:id/boot.
//
// Per ai.knowledge/global rule reference-rules-in-comments.
/* eslint-disable sort-exports/sort-exports */

// Lowered substring of the atob DOMException from mailroom-helper requester.ts.
export const ATOB_DOMEXCEPTION_SIGNATURE = 'string to be decoded is not correctly encoded';

// Total prewarm budget. Reduced from 5000ms to 2500ms since probes now run
// in parallel rather than sequentially — halves wall-clock time while still
// giving each Lambda a full 2500ms window to respond.
// Per ai.knowledge/global rule bias-towards-action.
export const HTTP_PREWARM_BUDGET_MS = 2500;

export const HTTP_PREWARM_POLL_MS = 250;

export const HTTP_PREWARM_MAX_STEPS = HTTP_PREWARM_BUDGET_MS / HTTP_PREWARM_POLL_MS;

/** Detects atob DOMException from mailroom-helper (non-base64 response body). */
export const isAtobDomException = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }

  return error.message.toLowerCase().includes(ATOB_DOMEXCEPTION_SIGNATURE);
};

/** Callable probe injected by the controller to exercise a mailroom RPC subject. */
export type MailroomProbeFn = (token: string) => Promise<unknown>;

/** Race sentinel: budget elapsed. */
const PROBE_TIMEOUT_SENTINEL = Symbol('probe-timeout');

/** Race sentinel: signal aborted. */
const PROBE_ABORT_SENTINEL = Symbol('probe-abort');

/**
 * Race a single probe against a budget timer + abort signal.
 * Returns 'warmed' (probe resolved or threw), 'timeout', or 'aborted'.
 * Any thrown error counts as warm (Lambda responded).
 */
const runSingleProbe = async (
  token: string,
  signal: AbortSignal,
  probe: MailroomProbeFn,
  perProbeBudgetMs: number
): Promise<'warmed' | 'timeout' | 'aborted'> => {
  // Already aborted.
  if (signal.aborted) {
    return 'aborted';
  }

  // No time left.
  if (perProbeBudgetMs <= 0) {
    return 'timeout';
  }

  // eslint-disable-next-line promise/avoid-new -- wraps thrown errors as 'warmed' signals.
  const warmPromise = new Promise<'warmed'>((resolve) => {
    probe(token)
      .then(() => resolve('warmed'))
      .catch((probeError: unknown) => {
        // Probe rejection. Most rejections (sentinel zero-ID 4xx, expected
        // network jitter) prove the Lambda responded → warm. The exception
        // is atob DOMException: the response body was non-base64, which is
        // a real corruption signal, not a warm-up signal. We still resolve
        // 'warmed' for the prewarm loop (the loop's job is "did SOMETHING
        // come back from the Lambda"), but log distinctly so debugging the
        // downstream linkAgent failure is unambiguous.
        if (isAtobDomException(probeError)) {
          console.warn(
            '[auto-link] Mailroom HTTP pre-warm probe: non-base64 body (atob DOMException) — real corruption, not a warm-up signal'
          );
        } else {
          console.debug(
            '[auto-link] Mailroom HTTP pre-warm probe: other error; Lambda responded',
            probeError
          );
        }
        resolve('warmed');
      });
  });

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  // eslint-disable-next-line promise/avoid-new -- timer race promise.
  const timeoutPromise = new Promise<typeof PROBE_TIMEOUT_SENTINEL>((resolve) => {
    timeoutId = setTimeout(() => {
      resolve(PROBE_TIMEOUT_SENTINEL);
    }, perProbeBudgetMs);
  });

  let abortListener: (() => void) | undefined;
  // eslint-disable-next-line promise/avoid-new -- abort signal race promise.
  const abortPromise = new Promise<typeof PROBE_ABORT_SENTINEL>((resolve) => {
    abortListener = (): void => {
      resolve(PROBE_ABORT_SENTINEL);
    };
    signal.addEventListener('abort', abortListener);
  });

  try {
    const outcome = await Promise.race([warmPromise, timeoutPromise, abortPromise]);
    if (outcome === PROBE_TIMEOUT_SENTINEL) {
      return 'timeout';
    }
    if (outcome === PROBE_ABORT_SENTINEL) {
      return 'aborted';
    }
    return outcome;
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
    if (abortListener !== undefined) {
      signal.removeEventListener('abort', abortListener);
    }
  }
};

/**
 * Bounded pre-warm for mailroom HTTP endpoint. Runs all probes in parallel
 * within a shared budget (each probe gets the full budget since they race
 * concurrently). Returns 'ready' when all probes have been exercised or
 * timed out, 'timeout' on abort or empty array.
 *
 * Optimization: probes previously ran sequentially (2.5s + 2.5s = 5s worst
 * case). Running in parallel cuts wall-clock time to max(probe_time) which
 * is at most the budget (2.5s). Backwards compatible — if Promise.all rejects
 * unexpectedly, we still return 'ready' since any probe activity warms the
 * Lambda.
 */
export const prewarmMailroomHttpEndpoint = async (
  token: string,
  signal: AbortSignal,
  probes: readonly MailroomProbeFn[]
): Promise<'ready' | 'timeout'> => {
  const overallStart = Date.now();

  // Empty array = nothing to warm.
  if (probes.length === 0) {
    console.warn(
      '[auto-link] Mailroom HTTP pre-warm called with empty probes array; returning timeout'
    );
    return 'timeout';
  }

  // Already aborted.
  if (signal.aborted) {
    return 'timeout';
  }

  // All probes run in parallel, each with the full budget. Since they run
  // concurrently, wall-clock time is max(probe_time) not sum(probe_time).
  const probePromises = probes.map((probe) =>
    runSingleProbe(token, signal, probe, HTTP_PREWARM_BUDGET_MS)
  );

  const outcomes = await Promise.all(probePromises);

  // If all probes were aborted, report timeout.
  if (outcomes.every((outcome) => outcome === 'aborted')) {
    return 'timeout';
  }

  const elapsed = Date.now() - overallStart;

  // Probe loop completed.
  console.debug(
    `[auto-link] Mailroom HTTP pre-warm finished after ${elapsed}ms across ${probes.length} probe(s)`
  );

  return 'ready';
};

/**
 * Backoff for linkAgent retry. ALL errors get bounded linear backoff,
 * including atob DOMException.
 *
 * History: an earlier version short-circuited atob DOMException to 0ms
 * because the prevailing theory was "Lambda already warmed by prewarm,
 * just retry instantly". That was wrong. atob DOMException means the
 * response body was non-base64 — typically because the cold Lambda's
 * proto-base64 marshaling layer hadn't fully initialized. Three instant
 * retries finished inside ~1s and the next request hit the same
 * un-initialized Lambda → Mac cold-start auto-link failed, app got stuck
 * on /room/:id/boot, AccountDropdown never mounted, app-launch:62 timed
 * out at 140s.
 *
 * Linear backoff (2s, 4s, 6s) gives the Lambda time to fully initialize
 * its mailroom marshaling layer between retries.
 *
 * Per ai.knowledge/global rule timeouts-are-bandaids — root-causing here
 * (classify decode error as real failure) instead of widening test budget.
 */
export const computeLinkRetryBackoffMs = (
  linkError: unknown,
  linkAttempt: number,
  backoffBaseMs: number
): number => {
  // void to keep the parameter as a contract signal — the classifier still
  // exists for distinct logging; backoff is uniform across all errors.
  void linkError;
  return backoffBaseMs * linkAttempt;
};
